import { useState } from "react";
import {
  DATE_FORMAT_LABELS,
  EXPORT_SOURCES,
  EXPORT_SOURCE_LABELS,
  MAX_EXPORT_COLUMNS,
  PRIVATE_EXPORT_SOURCES,
  type ExportColumn,
  type ExportDateFormat,
  type ExportSource,
  type ExportTemplate,
  type ExportTemplateSpec,
} from "@iltocat/shared";
import { ApiError, api } from "../lib/api.js";
import { Icon } from "./Icon.js";
import { Notice } from "./Notice.js";
import { Panel } from "./Panel.js";
import { useToast } from "./Toast.js";

interface ColumnDraft extends ExportColumn {
  key: number;
}

let nextKey = 0;
const keyed = (column: ExportColumn): ColumnDraft => ({ ...column, key: (nextKey += 1) });

/**
 * A custom export shape: which columns, in which order, headed what. Every
 * one starts as a copy of another -- a preset or one of your own -- because
 * "the QuickBooks one, but with our item codes" is how these actually get
 * made, and a blank grid of columns is how they do not.
 *
 * The presets themselves are never edited. Saving under an existing custom
 * template's name updates it, the same way field templates work.
 *
 * Private columns (cost, vendor, the vendor's invoice number) are allowed --
 * this file goes to the company's own books -- and marked, so choosing one
 * is never an accident.
 */
export function TemplateEditor({
  templates,
  onSaved,
}: {
  templates: ExportTemplate[];
  onSaved: (templates: ExportTemplate[], name: string) => void;
}) {
  const [draft, setDraft] = useState<
    (Omit<ExportTemplateSpec, "columns"> & { columns: ColumnDraft[] }) | null
  >(null);
  const [from, setFrom] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const toast = useToast();
  const own = templates.filter((template) => !template.builtIn);

  function start(id: string) {
    const source = templates.find((template) => template.id === id);
    if (!source) return;
    setError(null);
    setDraft({
      name: source.builtIn ? `${source.name.replace(/ \(starting point\)$/, "")} (ours)` : source.name,
      dateFormat: source.dateFormat,
      dueDays: source.dueDays,
      invoicePrefix: source.invoicePrefix,
      columns: source.columns.map(keyed),
    });
  }

  const setColumn = (key: number, patch: Partial<ExportColumn>) =>
    setDraft((now) =>
      now
        ? {
            ...now,
            columns: now.columns.map((column) => (column.key === key ? { ...column, ...patch } : column)),
          }
        : now,
    );

  const move = (index: number, by: -1 | 1) =>
    setDraft((now) => {
      if (!now) return now;
      const columns = [...now.columns];
      const [taken] = columns.splice(index, 1);
      if (!taken) return now;
      columns.splice(index + by, 0, taken);
      return { ...now, columns };
    });

  async function save(event: React.FormEvent) {
    event.preventDefault();
    if (!draft) return;
    setBusy(true);
    setError(null);
    try {
      const spec: ExportTemplateSpec = {
        ...draft,
        columns: draft.columns.map(({ header, source, value }) =>
          source === "constant" ? { header, source, value: value ?? "" } : { header, source },
        ),
      };
      const { templates: next } = await api.billing.saveTemplate(spec);
      onSaved(next, draft.name);
      toast("Template saved.");
      setDraft(null);
    } catch (problem) {
      setError(problem instanceof ApiError ? problem.message : "That template did not save.");
    } finally {
      setBusy(false);
    }
  }

  async function remove(id: string) {
    try {
      const { templates: next } = await api.billing.removeTemplate(id);
      onSaved(next, "");
      toast("Template deleted.");
    } catch (problem) {
      setError(problem instanceof ApiError ? problem.message : "That did not delete.");
    }
  }

  return (
    <Panel
      title="Export templates"
      note="The presets cannot be changed. Copy one, rename the columns to match your billing system's import, and save it as your own."
    >
      {own.length > 0 && (
        <ul className="rows">
          {own.map((template) => (
            <li key={template.id} className="row-item">
              <span className="row-icon">
                <Icon name="clipboard" size={18} />
              </span>
              <span className="row-text">
                <strong>{template.name}</strong>
                <span className="muted small">
                  {template.columns.map((column) => column.header).join(", ")}
                </span>
              </span>
              <button
                type="button"
                className="button button-ghost button-small"
                onClick={() => start(template.id)}
              >
                Edit
              </button>
              <button
                type="button"
                className="icon-button icon-button-sm icon-button-danger"
                aria-label={`Delete ${template.name}`}
                onClick={() => void remove(template.id)}
              >
                <Icon name="trash" size={16} />
              </button>
            </li>
          ))}
        </ul>
      )}

      {draft === null ? (
        <div className="inline-add">
          <label className="field-label" htmlFor="template-from">
            Start from
          </label>
          <select
            id="template-from"
            className="input"
            value={from}
            onChange={(event) => setFrom(event.target.value)}
          >
            <option value="">Pick a template</option>
            {templates.map((template) => (
              <option key={template.id} value={template.id}>
                {template.name}
              </option>
            ))}
          </select>
          <button type="button" className="button" disabled={!from} onClick={() => start(from)}>
            Copy
          </button>
        </div>
      ) : (
        <form className="stack template-form" onSubmit={save}>
          <div className="split">
            <label className="field">
              <span className="field-label">Name</span>
              <input
                className="input"
                required
                value={draft.name}
                onChange={(event) => setDraft({ ...draft, name: event.target.value })}
              />
            </label>
            <label className="field">
              <span className="field-label">Dates look like</span>
              <select
                className="input"
                value={draft.dateFormat}
                onChange={(event) =>
                  setDraft({ ...draft, dateFormat: event.target.value as ExportDateFormat })
                }
              >
                {(Object.keys(DATE_FORMAT_LABELS) as ExportDateFormat[]).map((format) => (
                  <option key={format} value={format}>
                    {DATE_FORMAT_LABELS[format]}
                  </option>
                ))}
              </select>
            </label>
            <label className="field">
              <span className="field-label">Due after (days)</span>
              <input
                className="input input-number"
                type="number"
                min={0}
                max={365}
                value={draft.dueDays}
                onChange={(event) => setDraft({ ...draft, dueDays: Number(event.target.value) || 0 })}
              />
            </label>
            <label className="field">
              <span className="field-label">Invoice number prefix</span>
              <input
                className="input"
                value={draft.invoicePrefix}
                onChange={(event) => setDraft({ ...draft, invoicePrefix: event.target.value })}
              />
            </label>
          </div>

          <ol className="template-columns">
            {draft.columns.map((column, index) => (
              <li key={column.key} className="template-column">
                <input
                  className="input input-small"
                  aria-label={`Column ${index + 1} heading`}
                  value={column.header}
                  onChange={(event) => setColumn(column.key, { header: event.target.value })}
                />
                <select
                  className="input input-small"
                  aria-label={`Column ${index + 1} is filled from`}
                  value={column.source}
                  onChange={(event) => setColumn(column.key, { source: event.target.value as ExportSource })}
                >
                  {EXPORT_SOURCES.map((source) => (
                    <option key={source} value={source}>
                      {EXPORT_SOURCE_LABELS[source]}
                    </option>
                  ))}
                </select>
                {column.source === "constant" && (
                  <input
                    className="input input-small"
                    aria-label={`Column ${index + 1} text`}
                    placeholder="Text for every row"
                    value={column.value ?? ""}
                    onChange={(event) => setColumn(column.key, { value: event.target.value })}
                  />
                )}
                {PRIVATE_EXPORT_SOURCES.includes(column.source) && (
                  <span
                    className="pill pill-warn"
                    title="Private: fine for your own books, never for a customer"
                  >
                    <Icon name="lock" size={12} /> Private
                  </span>
                )}
                <span className="template-column-tools">
                  <button
                    type="button"
                    className="icon-button icon-button-sm"
                    aria-label={`Move column ${index + 1} earlier`}
                    disabled={index === 0}
                    onClick={() => move(index, -1)}
                  >
                    <Icon name="chevron-left" size={16} />
                  </button>
                  <button
                    type="button"
                    className="icon-button icon-button-sm"
                    aria-label={`Move column ${index + 1} later`}
                    disabled={index === draft.columns.length - 1}
                    onClick={() => move(index, 1)}
                  >
                    <Icon name="chevron-right" size={16} />
                  </button>
                  <button
                    type="button"
                    className="icon-button icon-button-sm icon-button-danger"
                    aria-label={`Remove column ${index + 1}`}
                    disabled={draft.columns.length === 1}
                    onClick={() =>
                      setDraft({ ...draft, columns: draft.columns.filter((one) => one.key !== column.key) })
                    }
                  >
                    <Icon name="x" size={16} />
                  </button>
                </span>
              </li>
            ))}
          </ol>

          {error && <Notice tone="error">{error}</Notice>}
          <div className="panel-foot">
            <button
              type="button"
              className="button button-quiet"
              disabled={draft.columns.length >= MAX_EXPORT_COLUMNS}
              onClick={() =>
                setDraft({
                  ...draft,
                  columns: [...draft.columns, keyed({ header: "New column", source: "title" })],
                })
              }
            >
              <Icon name="plus" size={18} />
              Add a column
            </button>
            <button
              type="button"
              className="button button-quiet"
              onClick={() => setDraft(null)}
              disabled={busy}
            >
              Cancel
            </button>
            <button type="submit" className="button" disabled={busy}>
              {busy ? "Saving…" : "Save template"}
            </button>
          </div>
        </form>
      )}
      {draft === null && error && <Notice tone="error">{error}</Notice>}
    </Panel>
  );
}
